import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import algoService from "@/services/algoService";
import customerAlgoService from "@/services/customerAlgoService";
import brokerAccountService from "@/services/brokerAccountService";
import type { Algo, AlgoTag, CustomerAlgoActivationRead } from "@/types";
import { extractErrorMessage } from "@/utils/errors";
import useAuth from "@/hooks/useAuth";
import HelpGuideLink from "@/components/HelpGuideLink";

type FilterTab = "ALL" | AlgoTag;

const FILTER_TABS: { value: FilterTab; label: string }[] = [
  { value: "ALL", label: "All" },
  { value: "HIGH_ALPHA", label: "High Alpha" },
  { value: "LOW_RISK", label: "Low Risk" },
  { value: "LOW_CAPITAL", label: "Low Capital" },
];

const TAG_LABELS: Record<AlgoTag, string> = {
  HIGH_ALPHA: "High Alpha",
  LOW_RISK: "Low Risk",
  LOW_CAPITAL: "Low Capital",
};

function formatInr(value: string): string {
  const num = Number.parseFloat(value);
  if (Number.isNaN(num)) return value;
  return `₹${num.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

/**
 * Customer Algo Control CTA block -- renders in place of the old always-
 * decorative `cta_button_text` button for any algo with a real Algo Engine
 * strategy wired up (`algo.strategy_key` set). No confirmation dialog for
 * either action, by design: clicking Activate/Deactivate takes effect
 * immediately. Server-side authorization and idempotency (never a
 * duplicate activation, cross-user isolation) are enforced by
 * `POST /customer/algo/{algo_id}/activate|deactivate` itself -- this
 * component only reflects whatever state that endpoint returns.
 */
function CustomerAlgoControl({
  algo,
  activation,
  canActivate,
  busy,
  onActivate,
  onDeactivate,
}: {
  algo: Algo;
  activation?: CustomerAlgoActivationRead;
  canActivate: boolean;
  busy: boolean;
  onActivate: (algo: Algo) => void;
  onDeactivate: (algo: Algo) => void;
}) {
  const status = activation?.status ?? "STOPPED";
  const isActive = status === "ACTIVE";
  const isError = status === "ERROR";
  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={0.5}>
        <Chip
          size="small"
          label={isError ? "Error" : isActive ? "Active" : "Stopped"}
          color={isError ? "error" : isActive ? "success" : "default"}
        />
        {isActive && activation?.activated_at && (
          <Typography variant="caption" color="text.secondary">
            Since {formatDate(activation.activated_at)}
          </Typography>
        )}
        {!isActive && !isError && activation?.deactivated_at && (
          <Typography variant="caption" color="text.secondary">
            Stopped {formatDate(activation.deactivated_at)}
          </Typography>
        )}
      </Stack>
      {isError && activation?.error_message && (
        <Typography variant="caption" color="error.main">
          {activation.error_message}
        </Typography>
      )}
      {isActive ? (
        <Button
          variant="outlined"
          color="error"
          fullWidth
          disabled={busy}
          onClick={() => onDeactivate(algo)}
          startIcon={busy ? <CircularProgress size={16} /> : undefined}
        >
          Deactivate
        </Button>
      ) : (
        <Tooltip title={canActivate ? "" : "Connect and set a default broker account to activate this strategy."}>
          <span>
            <Button
              variant="contained"
              color="secondary"
              fullWidth
              disabled={busy || !canActivate}
              onClick={() => onActivate(algo)}
              startIcon={busy ? <CircularProgress size={16} /> : undefined}
            >
              {isError ? "Retry Activate" : "Activate"}
            </Button>
          </span>
        </Tooltip>
      )}
    </Stack>
  );
}

function AlgoDetailCard({
  algo,
  activation,
  canActivate,
  busy,
  onActivate,
  onDeactivate,
  onManage,
  canManage,
}: {
  algo: Algo;
  activation?: CustomerAlgoActivationRead;
  canActivate: boolean;
  busy: boolean;
  onActivate: (algo: Algo) => void;
  onDeactivate: (algo: Algo) => void;
  onManage: (algo: Algo) => void;
  canManage: boolean;
}) {
  const pnl = Number.parseFloat(algo.pnl_amount);
  const pnlPositive = !Number.isNaN(pnl) && pnl >= 0;
  // Only the algo row with a real Algo Engine strategy wired up
  // (`strategy_key` set — TEK-MB01 / "Velocity Edge" today) gets a
  // functional CTA: customers get ACTIVATE/DEACTIVATE, admins additionally
  // get a "Manage (Admin)" link into the internal admin page. Every other
  // row's button stays purely decorative until it gets its own
  // strategy_key.
  const hasEngine = Boolean(algo.strategy_key);
  return (
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Typography variant="subtitle1" fontWeight={700}>
            {algo.algo_name}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1} mb={2}>
          {algo.tags.map((tag) => (
            <Chip key={tag} label={TAG_LABELS[tag] ?? tag} size="small" color="secondary" variant="outlined" />
          ))}
        </Stack>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ bgcolor: "action.hover", borderRadius: 1, px: 1.5, py: 1, mb: 2 }}
        >
          <Typography variant="caption" color="text.secondary">
            P&amp;L Since {formatDate(algo.pnl_since_date)}
          </Typography>
          <Typography variant="subtitle2" fontWeight={700} color={pnlPositive ? "success.main" : "error.main"}>
            {pnlPositive ? "+" : ""}
            {formatInr(algo.pnl_amount)}
          </Typography>
        </Box>
        <Grid container spacing={1.5} mb={2}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              XIRR
            </Typography>
            <Typography variant="body1" fontWeight={700}>
              {algo.xirr != null ? `${algo.xirr}%` : "—"}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Capital Required
            </Typography>
            <Typography variant="body1" fontWeight={700}>
              {formatInr(algo.capital_required)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Calmar Ratio
            </Typography>
            <Typography variant="body1" fontWeight={700}>
              {algo.calmar_ratio ?? "—"}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Avg Monthly Payout
            </Typography>
            <Typography variant="body1" fontWeight={700}>
              {algo.avg_monthly_payout != null ? formatInr(algo.avg_monthly_payout) : "—"}
            </Typography>
          </Grid>
        </Grid>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {algo.description}
        </Typography>
        {hasEngine ? (
          <Stack spacing={1}>
            <CustomerAlgoControl
              algo={algo}
              activation={activation}
              canActivate={canActivate}
              busy={busy}
              onActivate={onActivate}
              onDeactivate={onDeactivate}
            />
            {canManage && (
              <Button size="small" variant="text" fullWidth onClick={() => onManage(algo)}>
                Manage (Admin)
              </Button>
            )}
          </Stack>
        ) : (
          algo.cta_button_text && (
            <Button variant="contained" color="secondary" fullWidth disabled>
              {algo.cta_button_text}
            </Button>
          )
        )}
      </CardContent>
    </Card>
  );
}

export function StrategiesPage() {
  const [algos, setAlgos] = useState<Algo[]>([]);
  const [activations, setActivations] = useState<Record<string, CustomerAlgoActivationRead>>({});
  const [hasConnectedDefaultBroker, setHasConnectedDefaultBroker] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyAlgoId, setBusyAlgoId] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterTab>("ALL");
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const canManage = hasRole("ADMIN");

  function handleManage(algo: Algo) {
    navigate(`/admin/algos/${algo.id}`);
  }

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [algoResponse, activationResponse, brokerResponse] = await Promise.all([
        algoService.listActive(),
        customerAlgoService.listActivations().catch(() => ({ items: [] })),
        brokerAccountService.list().catch(() => ({ items: [] })),
      ]);
      setAlgos(algoResponse.items);
      const map: Record<string, CustomerAlgoActivationRead> = {};
      for (const item of activationResponse.items) {
        map[item.algo_id] = item;
      }
      setActivations(map);
      setHasConnectedDefaultBroker(
        brokerResponse.items.some((account) => account.is_default && account.is_connected)
      );
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function handleActivate(algo: Algo) {
    setBusyAlgoId(algo.id);
    setActionError(null);
    try {
      const updated = await customerAlgoService.activate(algo.id);
      setActivations((prev) => ({ ...prev, [algo.id]: updated }));
    } catch (err) {
      setActionError(extractErrorMessage(err));
    } finally {
      setBusyAlgoId(null);
    }
  }

  async function handleDeactivate(algo: Algo) {
    setBusyAlgoId(algo.id);
    setActionError(null);
    try {
      const result = await customerAlgoService.deactivate(algo.id);
      setActivations((prev) => ({ ...prev, [algo.id]: result.activation }));
      // 2026-09-08 Deactivate-and-Flatten: surface anything that could NOT
      // be confirmed flat -- never silently swallow a failed flatten just
      // because the status flip itself succeeded (see
      // `app.services.algo_deactivate_flatten_service`'s own docstring).
      if (!result.flatten_summary.all_flat) {
        const failing = result.flatten_summary.positions.filter((p) => p.outcome === "FAILED");
        setActionError(
          failing.length > 0
            ? `Deactivated, but ${failing.length} position(s) could not be confirmed flat: ${failing
                .map((p) => p.detail ?? p.outcome)
                .join("; ")}. These remain under normal risk management.`
            : "Deactivated, but one or more positions are still being closed -- check again shortly."
        );
      }
    } catch (err) {
      setActionError(extractErrorMessage(err));
    } finally {
      setBusyAlgoId(null);
    }
  }

  const filteredAlgos = useMemo(() => {
    if (filter === "ALL") return algos;
    return algos.filter((algo) => algo.tags.includes(filter));
  }, [algos, filter]);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={1}>
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(_e, value) => value && setFilter(value)}
          size="small"
        >
          {FILTER_TABS.map((tab) => (
            <ToggleButton key={tab.value} value={tab.value}>
              {tab.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <HelpGuideLink section="algo-strategies" />
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {actionError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}
      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : filteredAlgos.length === 0 ? (
        <Typography variant="body1" color="text.secondary" textAlign="center" py={6}>
          No strategies found for this filter.
        </Typography>
      ) : (
        <Grid container spacing={3}>
          {filteredAlgos.map((algo) => (
            <Grid item xs={12} sm={6} md={4} key={algo.id}>
              <AlgoDetailCard
                algo={algo}
                activation={activations[algo.id]}
                canActivate={hasConnectedDefaultBroker}
                busy={busyAlgoId === algo.id}
                onActivate={handleActivate}
                onDeactivate={handleDeactivate}
                onManage={handleManage}
                canManage={canManage}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}

export default StrategiesPage;
